import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from '@mui/material';
import { Home, Security } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';

const PrivacyPolicyPage = () => {
  const theme = useTheme();

  const sections = [
    {
      title: "Information We Collect",
      content: [
        "Personal Information: When you register on Mall of Stones, we collect your name, email address, phone number, and billing/shipping addresses.",
        "Business Information: For sellers, we collect business registration details, tax identification numbers, and bank account information for payments.",
        "Transaction Data: We collect information about your purchases, sales, payment methods, and transaction history.",
        "Usage Data: We automatically collect information about how you use our platform, including IP address, browser type, device information, and pages visited.",
        "Communication Data: We store messages exchanged through our platform between buyers and sellers, customer support interactions, and feedback."
      ]
    },
    {
      title: "How We Use Your Information",
      content: [
        "Account Management: To create and maintain your account, verify your identity, and provide customer support.",
        "Transaction Processing: To facilitate purchases and sales, process payments, manage orders, and handle shipping.",
        "Platform Improvement: To analyze usage patterns, improve our services, develop new features, and enhance user experience.",
        "Communication: To send order confirmations, shipping updates, promotional offers, and important platform announcements.",
        "Security: To detect and prevent fraud, protect against unauthorized access, and ensure platform security.",
        "Legal Compliance: To comply with applicable laws, regulations, and legal processes."
      ]
    },
    {
      title: "Information Sharing and Disclosure",
      content: [
        "With Sellers: When you make a purchase, we share necessary information with sellers to fulfill your order.",
        "With Buyers: Sellers can access buyer information necessary for order fulfillment and customer service.",
        "Service Providers: We share information with trusted third-party service providers who help us operate our platform (payment processors, shipping companies, cloud storage providers).",
        "Legal Requirements: We may disclose information when required by law, court order, or to protect our rights and safety.",
        "Business Transfers: In case of merger, acquisition, or sale of assets, your information may be transferred to the new entity.",
        "We do not sell your personal information to third parties for marketing purposes."
      ]
    },
    {
      title: "Data Security",
      content: [
        "Encryption: We use SSL/TLS encryption to protect data transmission between your device and our servers.",
        "Secure Storage: Personal and financial information is stored on secure servers with restricted access.",
        "Payment Security: We comply with PCI DSS standards for handling payment card information.",
        "Access Controls: We implement strict access controls and authentication measures for our systems.",
        "Regular Audits: We conduct regular security audits and vulnerability assessments.",
        "Incident Response: We have procedures in place to respond to security incidents and data breaches."
      ]
    },
    {
      title: "Your Rights and Choices",
      content: [
        "Access: You can access and review your personal information through your account settings.",
        "Correction: You can update or correct your personal information at any time.",
        "Deletion: You can request deletion of your account and personal information, subject to legal and business requirements.",
        "Data Portability: You can request a copy of your data in a portable format.",
        "Marketing Opt-out: You can unsubscribe from promotional emails and marketing communications.",
        "Cookie Controls: You can manage cookie preferences through your browser settings."
      ]
    },
    {
      title: "Cookies and Tracking Technologies",
      content: [
        "Essential Cookies: Required for basic platform functionality, user authentication, and security.",
        "Analytics Cookies: Help us understand how users interact with our platform to improve services.",
        "Advertising Cookies: Used to deliver relevant advertisements and measure campaign effectiveness.",
        "Third-party Cookies: Some features may use cookies from trusted partners (Google Analytics, payment processors).",
        "You can control cookie settings through your browser, but disabling certain cookies may affect platform functionality."
      ]
    },
    {
      title: "Data Retention",
      content: [
        "Account Information: Retained while your account is active and for a reasonable period after account closure.",
        "Transaction Records: Kept for 7 years for tax and legal compliance purposes.",
        "Communication Data: Stored for 3 years for customer service and dispute resolution.",
        "Usage Data: Aggregated and anonymized usage data may be retained indefinitely for analytics.",
        "Legal Hold: Data may be retained longer if required for legal proceedings or investigations."
      ]
    },
    {
      title: "International Data Transfers",
      content: [
        "Our servers are primarily located in India, and your data is processed in accordance with Indian data protection laws.",
        "We may transfer data to trusted service providers in other countries with adequate data protection measures.",
        "All international transfers are conducted with appropriate safeguards to protect your privacy rights.",
        "We comply with applicable cross-border data transfer regulations."
      ]
    },
    {
      title: "Children's Privacy",
      content: [
        "Mall of Stones is not intended for users under 18 years of age.",
        "We do not knowingly collect personal information from children under 18.",
        "If we become aware that we have collected information from a child under 18, we will delete it promptly.",
        "Parents or guardians who believe their child has provided information to us should contact us immediately."
      ]
    },
    {
      title: "Changes to This Privacy Policy",
      content: [
        "We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements.",
        "We will notify users of material changes through email or prominent notices on our platform.",
        "The updated policy will be effective from the date specified in the notice.",
        "Continued use of our platform after changes constitutes acceptance of the updated policy."
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Privacy Policy</Typography>
        </Breadcrumbs>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            sx={{
              p: 4,
              mb: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Security sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
            <Typography variant="h3" component="h1" gutterBottom sx={{ color:'whitesmoke',fontWeight: 'bold' }}>
              Privacy Policy
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9,color:'white', maxWidth: '800px', mx: 'auto' }}>
              Your privacy is important to us. This policy explains how Mall of Stones collects, 
              uses, and protects your personal information when you use our marketplace platform.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
              Last Updated: {new Date().toLocaleDateString('en-IN', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Typography>
          </Paper>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Mall of Stones ("we," "our," or "us") operates an online marketplace platform that connects 
              buyers and sellers of building materials, natural stones, marble, granite, and related products. 
              This Privacy Policy describes how we collect, use, disclose, and safeguard your information 
              when you visit our website or use our services.
            </Typography>
            <Typography variant="body1" paragraph>
              By using Mall of Stones, you consent to the data practices described in this policy. 
              If you do not agree with the practices described in this policy, please do not use our services.
            </Typography>
          </Paper>
        </motion.div>

        {/* Policy Sections */}
        {sections.map((section, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 * (index + 2) }}
          >
            <Paper sx={{ p: 4, mb: 3 }}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  fontWeight: 'bold', 
                  color: 'primary.main',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  pb: 1,
                  mb: 3
                }}
              >
                {index + 1}. {section.title}
              </Typography>
              <List>
                {section.content.map((item, itemIndex) => (
                  <ListItem key={itemIndex} sx={{ px: 0, py: 1 }}>
                    <ListItemText
                      primary={item}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '1rem',
                          lineHeight: 1.6,
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </motion.div>
        ))}

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 * (sections.length + 2) }}
        >
          <Paper sx={{ p: 4, backgroundColor: 'grey.50' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body1" paragraph>
                <strong>Email:</strong> connect@mallofstones.com
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Phone:</strong> +91 9460061819
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Address:</strong> Mall of Stones, Jaipur, Rajasthan, India
              </Typography>
              <Typography variant="body1">
                <strong>Business Hours:</strong> Monday to Saturday, 9:00 AM to 6:00 PM IST
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;